import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useEffect } from 'react';

export interface AISignal {
  id: string;
  symbol: string;
  side: 'buy' | 'sell' | 'hold';
  confidence: number;
  position_size_suggestion: number;
  reasoning: string;
  risk_factors: string[];
  market_context: Record<string, unknown>;
  status: 'pending' | 'approved' | 'rejected' | 'executed' | 'expired';
  reviewed_by: string | null;
  reviewed_at: string | null;
  expires_at: string;
  created_at: string;
}

export function useAISignals() {
  const queryClient = useQueryClient();

  const pendingQuery = useQuery({
    queryKey: ['ai-signals', 'pending'],
    queryFn: async (): Promise<AISignal[]> => {
      const { data, error } = await supabase
        .from('ai_signals')
        .select('*')
        .eq('status', 'pending')
        .gt('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(10);
      if (error) throw error;
      return (data ?? []) as unknown as AISignal[];
    },
    refetchInterval: 60000,
  });

  const recentQuery = useQuery({
    queryKey: ['ai-signals', 'recent'],
    queryFn: async (): Promise<AISignal[]> => {
      const { data, error } = await supabase
        .from('ai_signals')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(20);
      if (error) throw error;
      return (data ?? []) as unknown as AISignal[];
    },
    refetchInterval: 120000,
  });

  // Realtime subscription
  useEffect(() => {
    const channel = supabase
      .channel('ai-signals-realtime')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'ai_signals',
      }, () => {
        queryClient.invalidateQueries({ queryKey: ['ai-signals'] });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [queryClient]);

  const approveSignal = async (signalId: string, userId: string, newSize?: number) => {
    const update: Record<string, unknown> = {
      status: 'approved',
      reviewed_by: userId,
      reviewed_at: new Date().toISOString(),
    };
    if (typeof newSize === 'number' && Number.isFinite(newSize) && newSize > 0) {
      update.position_size_suggestion = newSize;
      update.modified_by = userId;
    }
    const { error } = await supabase
      .from('ai_signals')
      .update(update)
      .eq('id', signalId);
    if (error) throw error;
  };

  const rejectSignal = async (signalId: string, userId: string) => {
    const { error } = await supabase
      .from('ai_signals')
      .update({ status: 'rejected', reviewed_by: userId, reviewed_at: new Date().toISOString() })
      .eq('id', signalId);
    if (error) throw error;
  };

  return {
    pendingSignals: pendingQuery.data ?? [],
    recentSignals: recentQuery.data ?? [],
    isLoading: pendingQuery.isLoading,
    error: pendingQuery.error,
    approveSignal,
    rejectSignal,
  };
}
